import React, { useState, useEffect } from 'react';
import { Clock, Save, Users } from 'lucide-react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { getAppointmentSettings, updateAppointmentSettings } from '../../services/api';
import { toast } from 'sonner';

const AppointmentSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const res = await getAppointmentSettings();
      setSettings(res.data);
    } catch (e) {
      toast.error('Ayarlar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateAppointmentSettings(settings);
      toast.success('Randevu ayarları kaydedildi');
    } catch (e) {
      toast.error('Kaydetme hatası');
    } finally {
      setSaving(false);
    }
  };

  const updateHour = (dayIndex, field, value) => {
    setSettings(prev => ({
      ...prev,
      working_hours: prev.working_hours.map((h, i) =>
        i === dayIndex ? { ...h, [field]: value } : h
      )
    }));
  };

  const toggleClosed = (dayIndex) => {
    setSettings(prev => ({
      ...prev,
      working_hours: prev.working_hours.map((h, i) =>
        i === dayIndex ? { ...h, is_closed: !h.is_closed } : h
      )
    }));
  };

  if (loading) {
    return (
      <div className="ios-card text-center py-8">
        <p className="text-[#8E8E93]">Yükleniyor...</p>
      </div>
    );
  }

  if (!settings) return null;

  return (
    <div className="animate-fade-in space-y-4">
      {/* Concurrent Operations */}
      <div className="ios-card space-y-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-[#6b6de3]/10 rounded-lg flex items-center justify-center">
            <Users size={16} className="text-[#6b6de3]" />
          </div>
          <h2 className="text-sm font-semibold text-gray-900">Kapasite Ayarları</h2>
        </div>

        <div className="space-y-2">
          <Label className="text-xs text-[#8E8E93]">Maksimum Eşzamanlı İşlem</Label>
          <Input
            type="number"
            min="1"
            max="20"
            value={settings.max_concurrent_operations}
            onChange={e => setSettings(prev => ({
              ...prev,
              max_concurrent_operations: parseInt(e.target.value) || 1
            }))}
            className="h-10 text-sm bg-[#F2F2F7] border-0 rounded-xl w-24"
            data-testid="max-concurrent-input"
          />
          <p className="text-xs text-[#8E8E93]">
            Aynı anda kaç işlem yapılabileceğini belirler. Örn: 2 veteriner varsa 2 yazabilirsiniz.
          </p>
        </div>

        <div className="space-y-2">
          <Label className="text-xs text-[#8E8E93]">Randevu Slot Aralığı (dk)</Label>
          <Input
            type="number"
            min="5"
            max="120"
            step="5"
            value={settings.slot_interval_minutes}
            onChange={e => setSettings(prev => ({
              ...prev,
              slot_interval_minutes: parseInt(e.target.value) || 30
            }))}
            className="h-10 text-sm bg-[#F2F2F7] border-0 rounded-xl w-24"
            data-testid="slot-interval-input"
          />
          <p className="text-xs text-[#8E8E93]">
            Randevu saatleri bu aralıklarla gösterilir (ör: 30 dk = 09:00, 09:30, 10:00...).
          </p>
        </div>
      </div>

      {/* Working Hours */}
      <div className="ios-card space-y-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-[#FF9500]/10 rounded-lg flex items-center justify-center">
            <Clock size={16} className="text-[#FF9500]" />
          </div>
          <h2 className="text-sm font-semibold text-gray-900">Çalışma Saatleri</h2>
        </div>

        <div className="space-y-2">
          {settings.working_hours.map((day, idx) => (
            <div
              key={day.day}
              className={`flex items-center gap-3 p-3 rounded-xl transition-colors ${
                day.is_closed ? 'bg-red-50/50' : 'bg-[#F2F2F7]'
              }`}
              data-testid={`working-hour-day-${day.day}`}
            >
              <div className="w-24 shrink-0">
                <span className={`text-sm font-medium ${day.is_closed ? 'text-red-400' : 'text-gray-900'}`}>
                  {day.label}
                </span>
              </div>

              <div className="flex items-center gap-2 flex-1">
                {!day.is_closed ? (
                  <>
                    <Input
                      type="time"
                      value={day.open}
                      onChange={e => updateHour(idx, 'open', e.target.value)}
                      className="h-9 text-sm bg-white border-0 rounded-lg w-[110px] text-center"
                      data-testid={`open-time-${day.day}`}
                    />
                    <span className="text-xs text-[#8E8E93]">-</span>
                    <Input
                      type="time"
                      value={day.close}
                      onChange={e => updateHour(idx, 'close', e.target.value)}
                      className="h-9 text-sm bg-white border-0 rounded-lg w-[110px] text-center"
                      data-testid={`close-time-${day.day}`}
                    />
                  </>
                ) : (
                  <span className="text-xs text-red-400 font-medium">Kapalı</span>
                )}
              </div>

              <Switch
                checked={!day.is_closed}
                onCheckedChange={() => toggleClosed(idx)}
                data-testid={`toggle-day-${day.day}`}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Save Button */}
      <Button
        onClick={handleSave}
        disabled={saving}
        className="w-full h-14 text-base font-semibold bg-[#6b6de3] hover:bg-[#5658c4] rounded-full"
        data-testid="save-appointment-settings-btn"
      >
        <Save size={18} className="mr-2" />
        {saving ? 'Kaydediliyor...' : 'Kaydet'}
      </Button>
    </div>
  );
};

export default AppointmentSettings;
